import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../global-components/src";
import { useChatEcho } from "../../hooks/useChatEcho";
import { incrementGlobalChatBadge } from "../../hooks/UseNotifications";
import { MessageChat } from "../../types";
import { getChatListCacheKey } from "../../utils/chat-list-cache";

/**
 * Componente renderizado uma única vez no topo da árvore de rotas.
 * Mantém a conexão WebSocket (Laravel Reverb) sempre ativa quando o
 * usuário NÃO está nas telas /chat ou /chats (que têm seu próprio echo).
 *
 * Responsabilidades:
 *  1. Quando uma mensagem nova chega e o usuário está em outra tela →
 *     incrementa o badge de notificação de chat imediatamente.
 *  2. Atualiza o localStorage da lista de chats em tempo real para que
 *     ao navegar para /chats os dados já estejam frescos.
 *
 * Quando o usuário está em /chat/* ou /chats, este componente passa userId
 * como undefined para o hook, que retorna cedo sem criar conexão — evitando
 * conexões WebSocket duplicadas.
 */
const GlobalChatListener = () => {
    const { token, user } = useAuth();
    const location = useLocation();
    const userIdRef = useRef(user?.id);
    userIdRef.current = user?.id;

    // Extrai o chat_id que está aberto na URL, se houver (/chat/123)
    const openChatIdMatch = location.pathname.match(/^\/chat\/(\d+)$/);
    const openChatId = openChatIdMatch ? Number(openChatIdMatch[1]) : null;
    const openChatIdRef = useRef(openChatId);
    openChatIdRef.current = openChatId;

    // Nas rotas de chat já existe useChatEcho — não criar conexão dupla
    const isOnChatRoute =
        location.pathname.startsWith("/chat") ||
        location.pathname === "/chats";

    useChatEcho({
        // Passa undefined quando já há um echo ativo na rota de chat,
        // fazendo o hook retornar imediatamente sem conectar.
        userId: isOnChatRoute ? undefined : user?.id,
        token,
        onMessage: (chatId: number, message: MessageChat) => {
            const uid = userIdRef.current;
            if (!uid) return;

            // ── 1. Atualiza localStorage da lista de chats ────────────────────
            try {
                const raw = localStorage.getItem(getChatListCacheKey(uid));
                if (raw) {
                    const chats: any[] = JSON.parse(raw);
                    const idx = chats.findIndex((c) => c.chat_id === chatId);
                    if (idx !== -1) {
                        const [chat] = chats.splice(idx, 1);
                        chats.unshift({ ...chat, last_message: JSON.stringify(message) });
                        localStorage.setItem(getChatListCacheKey(uid), JSON.stringify(chats));
                    }
                }
            } catch { /* quota exceeded ou JSON inválido */ }

            // ── 2. Incrementa badge — só mensagens de outros usuários ─────────
            if (message.user_id !== uid) {
                incrementGlobalChatBadge(uid);
            }
        },
    });

    return null;
};

export default GlobalChatListener;
